import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Gauge, Boxes, Share2, Eye, Store, Users, Zap, Cpu } from 'lucide-react';
import Layout from '../components/Layout';
import SafeImage from '../components/SafeImage';
import { PLANS } from '../mock/mockData';
import { useAuth } from '../context/AuthContext';

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="font-mono-display text-2xl sm:text-3xl neon-orange">{value}</div>
    <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{label}</div>
  </div>
);

const FeatureTile = ({ icon: Icon, title, desc, accent = 'orange' }) => (
  <div className="neon-card rounded-md p-5 bracket-corners">
    <div className={`w-10 h-10 rounded grid place-items-center mb-3 ${accent==='cyan' ? 'bg-cyan-400/10 border border-cyan-400/40' : 'bg-orange-500/10 border border-orange-500/40'}`}>
      <Icon className={`w-5 h-5 ${accent==='cyan' ? 'text-cyan-300' : 'text-orange-300'}`} />
    </div>
    <h3 className="font-mono-display text-lg text-white mb-1">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  const { isAuthed } = useAuth();
  const primaryPath = isAuthed ? '/dashboard' : '/auth/register';
  const primaryText = isAuthed ? 'Dashboard' : 'Get Started';
  const planCtaPath = (planId) => (isAuthed ? (planId === 'free' ? '/dashboard' : '/pricing') : (planId === 'free' ? '/auth/register' : '/pricing'));
  const planCtaText = (cta) => (isAuthed && cta === 'Get Started' ? 'Dashboard' : cta);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-10 sm:pt-16 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="slide-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-200 text-xs uppercase tracking-widest mb-6">
                <Cpu className="w-3.5 h-3.5" /> Military-grade Valorant vault
              </div>
              <h1 className="font-mono-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-5">
                <span className="neon-orange glitch">Sync.</span>{' '}
                <span className="text-white">Track.</span>
                <br />
                <span className="neon-cyan">Flex.</span>
              </h1>
              <p className="text-zinc-300/90 text-lg max-w-xl mb-8 leading-relaxed">
                ValoRex is the ideal platform for checking stats, managing inventory &amp; store, and showcasing your Valorant accounts with cyberpunk-grade shareable pages.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-10">
                <Link to={primaryPath} className="btn-neon">
                  {primaryText} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="btn-ghost">View Plans</Link>
              </div>
              <div className="grid grid-cols-3 gap-4 max-w-md">
                <Stat value="5K+" label="Slots" />
                <Stat value="24/7" label="Uptime" />
                <Stat value="PKR" label="Billing" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 rounded-xl blur-3xl opacity-60" style={{background:'radial-gradient(closest-side, rgba(255,107,26,0.25), transparent 70%)'}} />
              <div className="relative neon-card rounded-lg p-5 scanlines overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"/>
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"/>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400"/>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-cyan-300">vault // live</span>
                </div>
                <div className="relative rounded overflow-hidden border border-orange-500/20">
                  <SafeImage src="https://media.valorant-api.com/playercards/17712b8a-4555-8b65-2bbe-75a288069420/wideart.png" alt="card" className="w-full h-40 object-cover" fallbackClassName="w-full h-40" fallbackIcon={ShieldCheck} fallbackLabel="No card image" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                    <div>
                      <div className="text-xs text-cyan-300/80 uppercase tracking-widest">Immortal 3</div>
                      <div className="font-mono-display text-xl text-white">YouZron <span className="text-orange-300">#LXRD</span></div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase text-zinc-400">Level</div>
                      <div className="font-mono-display text-xl neon-orange tabular">169</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  {['Vandal','Phantom','Operator'].map((w,i)=> (
                    <div key={w} className="rounded border border-cyan-400/20 bg-cyan-400/5 p-2">
                      <div className="text-[10px] uppercase tracking-widest text-cyan-300/80">{w}</div>
                      <div className="font-mono-display text-sm text-white">Skin #{i+1}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-zinc-400">
                  <span>Total VP: <span className="text-orange-300 tabular">24,685</span></span>
                  <span>Skins: <span className="text-cyan-300 tabular">38</span></span>
                  <span>Agents: <span className="text-cyan-300 tabular">23</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 sm:py-16 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <div className="text-xs uppercase tracking-widest text-cyan-300 mb-2">// features</div>
            <h2 className="font-mono-display text-3xl sm:text-4xl text-white">Everything you need to <span className="neon-orange">showcase</span></h2>
            <p className="text-zinc-400 mt-3">Manage inventories, share profiles, and flex your loadouts with a clean military interface.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureTile icon={Boxes} title="Manage Inventory" desc="Seamlessly browse skins, buddies, sprays, agents and cards across all your accounts." />
            <FeatureTile icon={Share2} title="Share Accounts" desc="Generate a shareable link to show off your Valorant profile, stats and Inventory." accent="cyan" />
            <FeatureTile icon={Store} title="Access Store" desc="Peek at daily offers &amp; Night Market deals without opening the game client." />
            <FeatureTile icon={Gauge} title="Detailed Stats" desc="Ranks, match history, peak ranks and lifetime stats – always up to date." accent="cyan" />
            <FeatureTile icon={ShieldCheck} title="Secure Storage" desc="Access tokens are handled with strict encryption and never shared publicly." />
            <FeatureTile icon={Users} title="ValoRex Community" desc="Join the ValoRex Discord for any kind of help including payment issues and reporting bugs." accent="cyan" />
          </div>
        </div>
      </section>

      {/* Pricing snapshot */}
      <section className="py-14 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">// pricing</div>
              <h2 className="font-mono-display text-3xl sm:text-4xl text-white">Pick your <span className="neon-orange">rank</span></h2>
            </div>
            <Link to="/pricing" className="btn-ghost">Compare plans <ArrowRight className="w-3.5 h-3.5"/></Link>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
            {PLANS.map((p) => {
              const cardGlow = p.id === 'platinum' ? 'pricing-card-blue' : p.id === 'immortal' ? 'pricing-card-red' : p.id === 'radiant' ? 'pricing-card-gold' : 'neon-card';
              return (
              <div key={p.id} className={`${cardGlow} rounded-md p-6 relative h-full flex flex-col`}>
                {p.id==='immortal' && (
                  <span className="absolute -top-3 left-6 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-orange-500 text-black rounded">Most popular</span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <SafeImage src={p.icon} alt={p.name} className="w-10 h-10 object-contain" fallbackClassName="w-10 h-10" fallbackIcon={ShieldCheck} />
                  <div>
                    <div className="font-mono-display text-xl text-white">{p.name}</div>
                    <div className="text-xs text-zinc-400">{p.tagline}</div>
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="font-mono-display text-3xl neon-orange">{p.priceLabel}</span>
                  <span className="text-xs text-zinc-400">/ month</span>
                </div>
                <ul className="space-y-2 text-sm text-zinc-300 mb-6">
                  {p.features.slice(0,5).map((f)=> (
                    <li key={f} className="flex items-start gap-2"><Eye className="w-3.5 h-3.5 text-cyan-300 mt-1"/>{f}</li>
                  ))}
                </ul>
                <Link to={planCtaPath(p.id)} className="btn-neon w-full justify-center mt-auto">{planCtaText(p.cta)}</Link>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative">
        <div className="max-w-5xl mx-auto px-6">
          <div className="neon-card rounded-lg p-8 sm:p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-40" style={{background:'radial-gradient(circle at 20% 20%, rgba(255,107,26,0.25), transparent 60%), radial-gradient(circle at 80% 80%, rgba(0,229,255,0.2), transparent 60%)'}} />
            <Zap className="w-8 h-8 mx-auto mb-3 text-orange-300" />
            <h3 className="font-mono-display text-2xl sm:text-3xl text-white mb-2 relative">Ready to flex your vault?</h3>
            <p className="text-zinc-400 max-w-xl mx-auto mb-6 relative">Join a unified command center for your Valorant identity, designed to help you track loadouts, stats, and accounts with ease.</p>
            <Link to={primaryPath} className="btn-neon relative">{isAuthed ? 'Go to Dashboard' : 'Sign Up Now'} <ArrowRight className="w-4 h-4"/></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
